import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { getDeclinePreviewApi } from "@/portals/patient/api/familyApi";
import { saveAuthFlowState } from "./authFlowState";
import { startGoogleSignIn } from "./googleAuth";
import { InviteUnavailableNotice } from "./InviteUnavailableNotice";
import { saneOtpSeconds } from "./otpConfig";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import { validateFields } from "@/shared/lib/validators";
import { AuthHeading } from "@/shared/components/AuthHeading";

interface InviteContext {
  patientName: string;
  categories: string[];
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export function PatientSignInPage() {
  const signIn = usePatientAuthStore((s) => s.signIn);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inviteContext, setInviteContext] = useState<InviteContext | null>(null);
  // True only when a token WAS present but didn't resolve to a live invite —
  // cancelled, declined, already used, or expired (mirrors PatientSignUpPage's
  // identical handling of its own accept_url token).
  const [inviteUnavailable, setInviteUnavailable] = useState(false);

  // Guards against React StrictMode's dev-only double-invocation of effects on
  // mount, which would otherwise fire this toast twice for the same notice.
  const noticeShownRef = useRef(false);
  useEffect(() => {
    if (noticeShownRef.current) return;
    const { notice, error } = (location.state as { notice?: string; error?: string } | null) ?? {};
    if (notice || error) {
      noticeShownRef.current = true;
      // error: a failed "Continue with Google" (see GoogleCallbackPage).
      if (notice) toastSuccess(notice);
      if (error) toastError(error);
      // Clear the notice from history state so it can't reappear (e.g. browser back/forward).
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolves the existing-account invite email's login_url token (see
  // FamilyService._send_invite_email) — purely a preview before login, same
  // division of responsibility as the signup page's accept_url token. The
  // actual accept still happens after login, from the dashboard (see
  // CaregiverDashboardView's auto-accept effect); this only shows who's
  // inviting, or that the invite is dead, before the patient even signs in.
  // Reuses the decline-preview endpoint since it already resolves either a
  // PENDING or AWAITING_SIGNUP invite read-only, with no side effects.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    getDeclinePreviewApi(inviteToken).then(({ data }) => {
      if (cancelled) return;
      if (!data) {
        setInviteUnavailable(true);
        return;
      }
      setEmail(data.email);
      setInviteContext({ patientName: data.patientName, categories: data.categories });
    });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  // Leaves for Google's account chooser; only comes back here on a failure to start.
  const [googleLoading, setGoogleLoading] = useState(false);
  const handleGoogle = async () => {
    setGoogleLoading(true);
    const error = await startGoogleSignIn();
    if (error) {
      setGoogleLoading(false);
      toastError(error);
    }
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { email, password },
      {
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
        password: [{ required: true, message: "Password is required" }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);

    if (result.success) {
      navigate("/patient", { replace: true });
      return;
    }

    if (result.code === "EMAIL_NOT_VERIFIED") {
      // The account already has a pending signup OTP that may well be minutes old —
      // seed the real remaining time from the backend so OtpPanel's countdown doesn't
      // restart from a fresh 5:00 for a code that's actually about to expire (or has
      // less time left than that). Falls back to a fresh window only if an older
      // backend build omits otpExpiresInSeconds on this error.
      const expiresInSeconds = saneOtpSeconds(result.otpExpiresInSeconds);
      saveAuthFlowState({ email, otpExpiresAt: Date.now() + expiresInSeconds * 1000 });
      navigate("/patient/auth/verify-otp", { state: { email, fromSignup: false } });
      return;
    }

    toastError(result.error ?? "Unable to sign in.");
  };

  if (inviteUnavailable) {
    return <InviteUnavailableNotice continueTo="/patient/auth/signin" continueLabel="Continue to a regular sign in" />;
  }

  return (
    <div className="w-full">
      <AuthHeading title={<>Welcome To <span className="font-extrabold tracking-tight">MedSyncIQ</span></>} subtitle="Please enter your details" className="mb-9" />

      {inviteContext && (
        <div className="!mb-6 flex items-start gap-2.5 rounded-[20px] border border-[#F36F8F]/40 bg-[#FFF7F8] p-3.5">
          <Icon icon="lucide:heart-handshake" className="mt-0.5 h-4 w-4 shrink-0 text-[#E4002B]" />
          <p className="text-xs leading-relaxed text-[#374151]">
            <span className="font-semibold">{inviteContext.patientName}</span> invited you to view their health records
            {inviteContext.categories.length > 0 && (
              <> — <span className="font-medium">{inviteContext.categories.join(", ")}</span></>
            )}
            . Sign in to accept.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <AuthTextField
          required
          label="Email"
          placeholder="Enter your email"
          value={email}
          onChange={(v) => { setEmail(v); clearError("email"); }}
          error={errors.email}
          disabled={loading}
        />

        <AuthPasswordField
          required
          label="Password"
          placeholder="Enter your password"
          value={password}
          onChange={(v) => { setPassword(v); clearError("password"); }}
          error={errors.password}
          disabled={loading}
        />

        {/* A button (not a Link) so it can be disabled while sign-in is in
            flight, same look as the OTP screen's disabled "Resend OTP". */}
        <div className="mb-7 flex items-center justify-end">
          <Button
            variant="link"
            disabled={loading}
            onClick={() => navigate("/patient/auth/forgot-password")}
            className="!h-auto !p-0 !text-sm !font-medium !text-[#E4002B] hover:!text-[#E4002B] hover:!underline disabled:!no-underline"
          >
            Forgot Password?
          </Button>
        </div>

        {/* Buttons wrapped in flex+gap so spacing can't collapse or get overridden */}
        <div className="flex flex-col gap-5">
          <AuthSubmitButton loading={loading}>Sign in</AuthSubmitButton>

          <Button
            variant="outline"
            disabled={loading || googleLoading}
            onClick={handleGoogle}
            className="!flex !h-auto !w-full !items-center !justify-center !gap-2.5 !rounded-[14px] !border-[#E5E7EB] !bg-white !py-3 !text-sm !font-medium !text-[#374151] hover:!border-[#E5E7EB] hover:!bg-[#F9FAFB] hover:!text-[#374151]"
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        </div>
      </form>

      <p className="!mt-5 text-center text-sm text-[#9CA3AF]">
        Don't have an account?{" "}
        <Button
          variant="link"
          disabled={loading}
          onClick={() => navigate("/patient/auth/signup")}
          className="!h-auto !p-0 !text-sm !font-semibold !text-[#E4002B] hover:!text-[#E4002B] hover:!underline disabled:!no-underline"
        >
          Sign up
        </Button>
      </p>
    </div>
  );
}
